import React, { useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { MessageSquare, BarChart3, CheckCircle, TrendingUp, RotateCcw, Target, BookOpen, Sparkles } from 'lucide-react';
import LandingStyleHero from '../components/productHero/LandingStyleHero';
import LaunchAnnouncementBanner from '../components/LaunchAnnouncementBanner';
import SEO from '../components/SEO';
import { PRODUCTION_PATHS, resolveHostedEmbedUrl } from '../config/siteUrl';
import { LoadingSpinner } from '../components/LoadingSpinner';
import PremiumAccessPrompt from '../components/PremiumAccessPrompt';
import { usePremiumProductAccess } from '../hooks/usePremiumProductAccess';
import { PREMIUM_PRODUCT_KEYS } from '../config/premiumProducts';

const sectionTitleClass =
  'text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent';

const glassCardClass =
  'rounded-2xl border border-slate-200/80 bg-white/95 shadow-lg shadow-slate-200/50 p-5 sm:p-6 md:p-8 transition-all duration-300 hover:shadow-xl hover:border-emerald-200/60 hover:-translate-y-0.5';

const whyDifferent = [
  { icon: MessageSquare, title: 'You drive the case', text: 'This is candidate-led practice. You choose the structure, ask for the right data, and move the discussion forward instead of waiting to be spoon-fed.' },
  { icon: RotateCcw, title: 'You get challenged before mistakes harden', text: 'Are You Sure? checks help catch issues like unit slips, scale confusion, or weak jumps before they drag the rest of your answer down.' },
  { icon: CheckCircle, title: 'You leave with a next step', text: 'Per-turn feedback points to what to fix now, so your next rep improves instead of feeling like more random practice.' },
];

const howItWorks = [
  { icon: BookOpen, title: 'Choose a BCG-style case', text: 'Start from the Casey case library so your assessment practice and live interview practice reinforce each other.' },
  { icon: MessageSquare, title: 'Run the interview live', text: 'Work through the case in a candidate-led format where you drive structure, ask for information, and explain your thinking out loud.' },
  { icon: BarChart3, title: 'Get structured feedback as you go', text: 'See where your structure, math, synthesis, or judgment held up and where they started to slip.' },
  { icon: TrendingUp, title: 'Track your progress over time', text: 'Use the dashboard to see what is improving, what still repeats, and where to focus before your next real interview.' },
];

const whatMakesBcg = [
  { icon: Target, title: 'Candidate-led from the start', text: 'You are expected to lead the case, not just respond. That changes how you think, ask questions, and manage the conversation.' },
  { icon: RotateCcw, title: 'AYS when it matters', text: 'When the system spots a likely mistake, it pushes you to check your thinking before moving on.' },
  { icon: BookOpen, title: 'Same case, two ways to practice', text: 'Drill the format in Casey Simulation, then run the same kind of case live here so your prep carries over instead of living in separate silos.' },
];

const relatedLinks = [
  { label: 'BCG Casey Simulation', path: '/bcg-casey-simulation', Icon: BookOpen },
  { label: 'BCG Casey Guide', path: '/bcg-casey-guide', Icon: BookOpen },
  { label: 'About BCG Casey', path: '/about-bcg-casey', Icon: Sparkles },
];

function withDemoParam(url) {
  return `${url}${url.includes('?') ? '&' : '?'}demo=1`;
}

/** Keep localhost fast in dev, allow explicit override, and avoid preview-origin iframe URLs. */
function getBcgCoachIframeSrc() {
  return withDemoParam(resolveHostedEmbedUrl({
    envName: 'REACT_APP_BCG_AI_COACH_DEMO_URL',
    localhostUrl: 'http://localhost:5176',
    productionPath: PRODUCTION_PATHS.bcgAICoach,
  }));
}

function getBcgCoachLaunchUrl() {
  return resolveHostedEmbedUrl({
    envName: 'REACT_APP_BCG_AI_COACH_DEMO_URL',
    localhostUrl: 'http://localhost:5176',
    productionPath: PRODUCTION_PATHS.bcgAICoach,
  });
}

function BCGCoachPreviewPanel() {
  return (
    <>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(2,6,23,0.08)_0%,rgba(5,22,16,0.12)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/50 to-transparent" />
      <div className="relative border-b border-emerald-500/20 p-4 text-white bg-slate-900/40">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold text-emerald-200/90">BCG Candidate-Led AI Coach</span>
          <span className="rounded-full bg-emerald-500/20 px-2.5 py-1 text-[11px] font-semibold text-emerald-200">Case library</span>
        </div>
      </div>
      <div className="relative p-4">
        <div className="rounded-2xl border border-white/15 overflow-hidden bg-white/5 backdrop-blur-sm ring-1 ring-white/5 shadow-[0_16px_36px_-24px_rgba(2,6,23,0.78)]">
          <img
            src="/images/landing/casey-coach-feedback.png"
            alt="BCG AI Coach — case library and feedback"
            className="w-full h-auto block"
          />
        </div>
      </div>
    </>
  );
}

const COGNIDOOR_SET_AUTH_TOKEN = 'cognidoor:setAuthToken';

export default function BCGAICoachPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const coachIframeRef = useRef(null);
  const coachIframeSrc = useMemo(getBcgCoachIframeSrc, []);
  const coachLaunchUrl = useMemo(getBcgCoachLaunchUrl, []);
  const coachAccess = usePremiumProductAccess(PREMIUM_PRODUCT_KEYS.bcgAICoach);

  const handleCoachIframeLoad = useCallback(() => {
    const win = coachIframeRef.current?.contentWindow;
    if (!win) return;
    const user = getAuth().currentUser;
    if (!user) return;
    const targetOrigin = coachIframeSrc.startsWith('http') ? new URL(coachIframeSrc).origin : window.location.origin;
    user.getIdToken().then((token) => {
      if (token) win.postMessage({ type: COGNIDOOR_SET_AUTH_TOKEN, token }, targetOrigin);
    }).catch(() => {});
  }, [coachIframeSrc]);

  const handleViewPricing = () => navigate('/', { state: { scrollToPricing: true } });
  const handleSeeDemo = () => navigate('/', { state: { scrollToLiveDemos: true, track: 'bcg', demoVariantBcg: 'aiCoach' } });
  const handleOpenCoach = () => {
    if (coachAccess.isLoading) return;
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    coachAccess.attemptAccess(
      () => {
        window.open(coachLaunchUrl, '_blank', 'noopener,noreferrer');
      },
      {
        returnTo,
        onDemo: handleSeeDemo,
      }
    );
  };
  const scrollToCaseLibrary = () => {
    const el = document.getElementById('bcg-proof');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <SEO
        title="BCG Candidate-Led AI Coach | Cognidoor"
        description="Practice BCG-style candidate-led case interviews with an AI coach. Structured feedback, AYS checks, and dashboard progress."
        url="/bcg-ai-coach"
      />
      <div className="bg-white text-gray-900 font-sans" style={{ '--cog-blue-1': '#277AB2', '--cog-blue-2': '#0B5996', '--cog-blue-3': '#082B4A' }}>
        <LandingStyleHero
          track="bcg"
          headline="Practise live BCG-style cases with coaching that catches weak thinking early."
          subheadline="Drive the case yourself, ask for data, and get coached feedback on structure, math, synthesis, and judgment—plus Are You Sure? checks that challenge likely mistakes before they harden."
          eyebrowChips={['Candidate-led', 'Are You Sure? coaching', 'Progress dashboard']}
          proofRow={[
            { value: 'Same Casey case library' },
            { value: 'Live interview + coached feedback' },
            { value: 'Track readiness over time' },
          ]}
          primaryCtaLabel="Open AI Coach"
          onPrimaryCta={scrollToCaseLibrary}
          secondaryCtaLabel="See demo"
          onSecondaryCta={handleSeeDemo}
          announcement={
            <LaunchAnnouncementBanner
              track="bcg"
              badge="Better BCG coaching"
              title="Practice how to think, not just what to answer."
              body="Run candidate-led cases with coaching that catches weak logic early, explains the thinking gap, and points you to the next move before the mistake hardens."
              primaryCta="Open BCG Coach"
              onPrimaryCta={scrollToCaseLibrary}
            />
          }
          previewChildren={<BCGCoachPreviewPanel />}
        />

        <section id="why-different" className="py-10 sm:py-14 bg-gradient-to-b from-white via-slate-50/50 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-8 sm:mb-10'}>Why this works better than generic AI case prep</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whyDifferent.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-emerald-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section id="bcg-proof" className="py-20 sm:py-28 bg-white px-4 sm:px-8 md:px-12 lg:px-16">
          <div className="max-w-[120rem] mx-auto">
            <h2 className={sectionTitleClass + ' text-center mb-8 sm:mb-10'}>Case library and feedback</h2>
            <p className="text-center text-gray-600 max-w-2xl mx-auto mb-12 sm:mb-14 text-sm sm:text-base">
              Choose from the same case library you use for assessment practice. Run a case live with the AI coach and see exactly where your structure, math, and synthesis hold up.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_0.9fr] gap-8 lg:gap-10 items-center">
              <div className="rounded-2xl border border-slate-200/80 bg-white/95 shadow-xl overflow-hidden">
                <div className="relative w-full aspect-[4/3] min-h-[400px] sm:min-h-[500px] lg:min-h-[560px] bg-slate-50 overflow-hidden">
                  <iframe
                    ref={coachIframeRef}
                    src={coachIframeSrc}
                    title="BCG AI Coach case library"
                    className="absolute inset-0 w-full h-full border-0"
                    loading="lazy"
                    onLoad={handleCoachIframeLoad}
                  />
                </div>
                <div className="px-4 py-4 border-t border-slate-100 bg-white flex justify-center">
                  <button
                    type="button"
                    onClick={handleOpenCoach}
                    disabled={coachAccess.isLoading}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-semibold rounded-xl text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 bg-gradient-to-r from-emerald-500 via-emerald-600 to-green-600 hover:from-emerald-600 hover:via-emerald-700 hover:to-green-700"
                  >
                    {coachAccess.isLoading ? (
                      <>
                        <LoadingSpinner size="sm" tone="onPrimary" className="shrink-0" />
                        Checking access...
                      </>
                    ) : (
                      'Open AI Coach'
                    )}
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                {[
                  { label: 'Structure', text: 'See whether your branches made the analysis clear and usable.' },
                  { label: 'Math', text: 'See whether your numbers supported the recommendation.' },
                  { label: 'Synthesis', text: 'See whether you landed the answer clearly.' },
                  { label: 'Next move', text: 'Know exactly what to change on the next rep.' },
                ].map((item, i) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.06 }}
                    className="flex gap-3 rounded-xl border border-slate-200/80 bg-white p-5 text-left"
                  >
                    <span className="text-emerald-600 font-semibold text-base shrink-0">{item.label}:</span>
                    <span className="text-gray-600 text-base">{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 sm:py-14 bg-gradient-to-b from-white via-slate-50/50 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-8 sm:mb-10'}>How it works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {howItWorks.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-emerald-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-10 sm:py-14 bg-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-8 sm:mb-10'}>Why this feels closer to real BCG prep</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whatMakesBcg.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={glassCardClass + ' text-left'}
              >
                <item.icon className="w-8 h-8 text-emerald-600 mb-3" strokeWidth={1.8} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="py-10 sm:py-14 bg-gradient-to-b from-white via-slate-50/30 to-white text-center px-4 sm:px-6 md:px-10">
          <h2 className={sectionTitleClass + ' mb-6 sm:mb-8'}>Build your BCG prep stack</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6 text-sm">
            Use the BCG coach when you want live practice with feedback. Add Casey Simulation when you want assessment-format reps built from the same prep logic.
          </p>
          <div className="flex flex-wrap justify-center gap-4 max-w-3xl mx-auto">
            {relatedLinks.map(({ label, path, Icon }) => (
              <motion.button
                key={path}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(path)}
                className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-gray-700 shadow-sm hover:border-emerald-300 hover:bg-emerald-50/50 hover:text-emerald-800 transition-colors"
              >
                <Icon className="w-4 h-4" strokeWidth={2} /> {label}
              </motion.button>
            ))}
          </div>
          <p className="mt-6 text-sm text-gray-500">
            <button type="button" onClick={handleViewPricing} className="font-semibold text-emerald-600 hover:text-emerald-700 underline underline-offset-2">
              See plans and pricing
            </button>
          </p>
        </section>
      </div>
      <PremiumAccessPrompt {...coachAccess.promptProps} />
    </>
  );
}
